import _ from 'lodash'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import router from './router'
import { asyncRouterMap, routerMap } from './router'
import { routerWhiteList } from './config/router'
import store from './store/index'
import { getToken, removeToken } from './utils/auth'
import { Message } from 'element-ui'
import i18n from '@/lang'
import { subscribeConfirm } from './utils/subscribe'
import type { Route as VueRoute } from 'vue-router'
import { hasPermission as _hasPermission } from './plugins/permission-manager/manager'
import { setDocumentTitle } from './utils/documentTitle'
import { advId, advCC } from '@com/utils/adv-storage'
import { checkOldDomainAndRedirect } from '@com/utils/domain-redirect'
import { playTurboNextHostList } from '@/config/host'
import {
  checkPlanCloseExpire,
  checkRechargePlanCloseExpire,
  checkRationPlanCloseExpire,
} from '@pt/store/user/helper'
import { setPlanExpireShowed, isPlanExpireShowed } from '@pt/utils/plan'
import { LogoutType } from '@pt/enums/logs'
import { LogoutLogOption } from '@pt/utils/log'
import { PermissionKey } from '@com/enums/user'
import { showInternationalMessage } from '@pt/utils/message'
import { NODE_ENV } from '@com/config/env'
import { processLogAsyncRouterMap } from '@com/utils/process-log-async-router'

// import redirect2cn from '@com/utils/redirect-to-cn'
import { initChannelList } from '@com/utils/channel'

NProgress.configure({ showSpinner: false })

function hasPermission(permissionRoles?: string[]) {
  if (!permissionRoles) return true
  if (permissionRoles.length === 0) {
    return false
  }
  // 只要用户拥有某路径路由配置中 roles 数组里面中所有某一项就认为该用户可访问该页面
  return _hasPermission(permissionRoles, 'or')
}

interface Route {
  name?: string
  path?: string
}

function logout(option: LogoutLogOption) {
  store.dispatch('LogOut', option)
}
const isGMaker = ({ path, name }: Route) => {
  if (name) {
    return name.indexOf('creative-service') === -1
  }
  if (path) {
    return path.indexOf('/creative-service') === -1
  }
  return false
}
const isDMaker = ({ path, name }: Route) => {
  if (name) {
    return name.indexOf('creative-service') === 0
  }
  if (path) {
    return path.indexOf('/creative-service') === 0
  }
  return false
}

/**
 * 定制服务页（creative-service） + 首页(home) + 过期后订阅页 + 密码设置页
 * onlineCheck 设置为free，其他登录后的页面onlineCheck设置为cost
 * @param param0
 * @returns
 */
const getModule = (route: VueRoute) => {
  // 没有的默认是cost
  const payment = route.meta?.payment || 'cost'

  return payment
}

router.beforeEach(async (to, from, next) => {
  NProgress.start()

  // 检查路径是否包含 /next
  if (to.path.includes('/nl')) {
    // 不包含 /next 时跳转
    // 注意：window.location 会完全离开当前应用
    const redirectUrl = `${playTurboNextHostList[NODE_ENV]}/#${to.fullPath}`
    console.log(redirectUrl, '跳转地址')
    console.log(NODE_ENV, '环境')
    window.location.href = redirectUrl
    return
  }

  // 切换默认title
  setDocumentTitle('Playturbo')

  const oldNext = next
  next = function (...args) {
    store.commit('UPDATE_CURRENT_ROUTER', to)
    return oldNext.apply(null, args)
  }
  const isDispatcherPage = to.name === 'home'

  const navigateToMakerLogin = () => {
    next({ path: '/login' })
    NProgress.done() // if current page is dashboard will not trigger	afterEach hook, so manually handle it
  }

  const navigateToCommonTemplateSubscribe = (keys) => {
    // 改成试用申请弹窗或者试用申请页
    subscribeConfirm({
      permissionKey: keys.join(','),
    })
    next(false)
    NProgress.done()
  }

  const navigateToDispatcher = () => {
    next({ name: 'home' })
    NProgress.done() // if current page is dashboard will not trigger	afterEach hook, so manually handle it
  }

  /**
   * 根据 roles 权限生成可访问的路由表
   */
  const generateRoutes = async (roles: string[]) => {
    await store.dispatch('GenerateRoutes', {
      roles,
      asyncRouterMap,
      routerMap,
    })
    router.addRoutes(store.getters.accessibleRouters) // 动态添加可访问路由表
    next({ ...to, replace: true }) // hack 方法 确保 addRoutes 已完成, set the replace: true so the navigation will not leave a history record
  }

  const getUserInfo = async () => {
    try {
      const userInfo = await store.dispatch('GetUserInfo')
      advId.set(userInfo.id)
      advCC.set(userInfo.cc)
      if (!userInfo.cc) {
        advCC.remove()
      }
      // 初始化渠道列表
      await initChannelList()
      const roles = (_.get(userInfo, 'permissions') || []).concat(['default'])
      await generateRoutes(roles)
      return userInfo
    } catch (err) {
      console.log(err)
      advId.remove()
      advCC.remove()
      logout({
        type: LogoutType.APIError,
        msg: String(err),
      })
    }
  }

  // 打印异步路由表
  processLogAsyncRouterMap(asyncRouterMap)
  // auth 页面的入口只有一个，素材仓库广告主管理
  // 进入 auth 页面清理 token，保证清理旧的登陆态
  if (to.name === 'auth') {
    removeToken()
    next()
    NProgress.done()
    return
  }
  const isLoginPages = /^\/(login)/.test(to.path)

  // 检查是否需要跳转到 playturbo.xxx域名
  checkOldDomainAndRedirect()

  // 已登录访问非白名单页面
  if (getToken() && routerWhiteList.indexOf(to.path) === -1) {
    if (store.getters.roles.length === 0) {
      getUserInfo().then((res) => {
        // redirect2cn(res.cc)

        if (!isPlanExpireShowed(res.id ?? 0)) {
          checkPlanCloseExpire(res)

          if (
            res.plans &&
            (checkRechargePlanCloseExpire(res.plans) ||
              checkRationPlanCloseExpire(res.plans))
          ) {
            setPlanExpireShowed(res.id)
          }
        }
      })
      return
    }

    const targetModule = getModule(to)
    const fromModule = getModule(from)
    store.commit('pt/updateModule', targetModule)
    store.commit('pt/websocket/updateRouter', to)

    // 无ps权限、gmaker权限跳转订阅逻辑，直接进行跳转，不检查后续心跳包、onlinecheck
    if (to.name === 'common-template.subscribe') {
      next()
      return
    }

    // 已登录 + 未激活心跳包激活心跳包
    if (!store.state.pt.websocket.isHeartBeatActive) {
      await store.dispatch('pt/websocket/activeHeartBeat', { router: to })
    }

    // 无权限
    const noAccess = !hasPermission(
      to.meta.keys?.filter(
        (key: string) => key !== PermissionKey.International,
      ),
    ) // is_allow_ps !== 1 && is_allow_gm !== 1
    // 套餐订阅后端会控制下发权限key，这里路由只判断是否具有页面需要的权限key
    if (noAccess) {
      navigateToCommonTemplateSubscribe(to.meta.keys)
      return
    }

    if (to.meta.keys?.includes(PermissionKey.International)) {
      if (!_hasPermission(to.meta.keys, 'and')) {
        showInternationalMessage()
        next(false)
        NProgress.done()
        return
      }
    }

    // 通用模板或定制模板页且切换模板
    if (
      (isGMaker({ name: to.name, path: to.path }) ||
        isDMaker({ name: to.name, path: to.path })) &&
      targetModule !== fromModule
    ) {
      const data = await store.dispatch('pt/websocket/onlineCheck', {
        module: targetModule,
      })
      if (data && data.code !== 0) {
        Message({
          message:
            (i18n.t('playTurbo.websocket.error.onlineCheck') as string) ||
            (i18n.t('common.request.networkError') as string),
          type: 'error',
          duration: 3 * 1000,
        })
        // 登录人数超过限制强制退出登录
        window.setTimeout(
          () =>
            logout({
              type: LogoutType.OverLimit,
              msg: '登录人数超过限制强制退出登录',
            }),
          2000,
        )
        return
      }
    }

    next()
  } else if (getToken() && isLoginPages) {
    // 如果从注册页面跳转过来的，则不跳转到首页
    if (to.query.login_from === 'register') {
      next()
    } else {
      navigateToDispatcher()
    }
  } else {
    if (routerWhiteList.indexOf(to.path) !== -1) {
      next()
    } else {
      navigateToMakerLogin() // 跳转到对外站登录页
    }
  }
})

router.afterEach(() => {
  NProgress.done() // 结束 Progress
})

router.onError(() => {
  NProgress.done() // 结束 Progress
  console.log('onError')
})
